/**
 * Bigint-safe JSON serialization for RAILGUN SDK RPCs that cross the
 * `chrome.runtime.connect` port between the UI/SW and the offscreen document.
 *
 * `chrome.runtime.sendMessage` / port `postMessage` use structured cloning,
 * which DOES support bigints — but only when both sides have the same JS
 * realm guarantees. In practice port traffic round-trips through Chrome's
 * IPC layer and bigints in nested objects sometimes drop to `null`. We
 * sidestep that by replacing bigints with sentinel objects on the way out
 * and reviving them on the way in.
 *
 * The sentinel is `{ __bigint: '<decimal-or-hex-string>' }`. We use a marker
 * object (not a string with a prefix) so it survives any further plain JSON
 * serialization the SDK or the messenger layer might do, and so it can't
 * collide with user-supplied strings.
 *
 * This helper is intentionally generic — it walks any plain-object/array
 * tree. It's NOT meant for class instances or DOM nodes; the SDK call
 * surface is plain values + bigints, so that's fine.
 */

const BIGINT_KEY = '__bigint'

type BigIntSentinel = { [BIGINT_KEY]: string }

function isBigIntSentinel(v: unknown): v is BigIntSentinel {
  return typeof v === 'object' && v !== null && typeof (v as any)[BIGINT_KEY] === 'string'
}

export function encodeBigInts(value: unknown): unknown {
  if (typeof value === 'bigint') {
    return { [BIGINT_KEY]: value.toString() }
  }
  if (Array.isArray(value)) {
    return value.map(encodeBigInts)
  }
  if (value && typeof value === 'object') {
    // Skip typed-array / date / map / set — none of those should appear in
    // SDK call args or returns. If they do the consumer will complain.
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      out[k] = encodeBigInts(v)
    }
    return out
  }
  return value
}

export function decodeBigInts(value: unknown): unknown {
  if (isBigIntSentinel(value)) {
    return BigInt(value[BIGINT_KEY])
  }
  if (Array.isArray(value)) {
    return value.map(decodeBigInts)
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      out[k] = decodeBigInts(v)
    }
    return out
  }
  return value
}
